import { useMemo, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import type {
  EntityAppliedIn,
  EntityField,
  EntityFieldType,
  EntityGroupListItem,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/features/desks/desks-queries';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
import { desksListQuery } from '@/features/tickets/tickets-queries';
import {
  ENTITIES_QUERY_KEY,
  FIELD_TYPE_LABELS,
  FIELD_TYPES,
  entityGroupsOptions,
} from './entities-queries';

/**
 * Editor de grupo de campos personalizados: nome + ativo + mesas (só Ticket;
 * vazio = todas) + lista de campos (criar/editar/excluir; opções em chips
 * quando lista de seleção). Criar salva o grupo e mantém o dialog aberto
 * para adicionar os campos.
 */
export function EntityGroupDialog({
  appliedIn,
  groupId: initialGroupId,
  onClose,
}: {
  appliedIn: EntityAppliedIn;
  groupId: number | null;
  onClose: () => void;
}) {
  const list = useQuery(entityGroupsOptions());
  const [groupId, setGroupId] = useState(initialGroupId);

  const group = groupId != null ? ((list.data ?? []).find((g) => g.id === groupId) ?? null) : null;
  const missing = groupId != null && !list.isPending && !group;

  return (
    <Dialog
      open
      onClose={onClose}
      title={group || groupId != null ? 'Editar grupo de campos' : 'Novo grupo de campos'}
      className="sm:max-w-2xl"
    >
      {groupId != null && list.isPending ? (
        <div className="space-y-2" aria-busy>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : missing ? (
        <EmptyState message="Grupo não encontrado — pode ter sido excluído" />
      ) : (
        // key: o formulário monta o estado uma vez por grupo (troca de grupo ou
        // "novo → criado" remonta com os valores do servidor).
        <GroupForm
          key={group?.id ?? 'new'}
          appliedIn={appliedIn}
          group={group}
          onCreated={setGroupId}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
}

/**
 * Formulário do grupo. Estado inicializado UMA vez a partir de `group` (o pai
 * remonta via `key`); a seleção de mesas parte de `group.deskIds` — nunca de
 * nomes — e ids fora da lista de mesas carregada são preservados no salvar.
 */
function GroupForm({
  appliedIn,
  group,
  onCreated,
  onClose,
}: {
  appliedIn: EntityAppliedIn;
  group: EntityGroupListItem | null;
  onCreated: (id: number) => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const desks = useQuery(desksListQuery());

  const [name, setName] = useState(group?.name ?? '');
  const [active, setActive] = useState(group?.active ?? true);
  // Vínculos por id, direto do servidor — independem da query de mesas (ela só
  // serve para renderizar os rótulos das checkboxes).
  const [deskIds, setDeskIds] = useState<number[]>(group?.deskIds ?? []);

  const initialDeskIds = group?.deskIds;
  const deskOptions = useMemo(
    () => (desks.data ?? []).filter((d) => d.active || (initialDeskIds?.includes(d.id) ?? false)),
    [desks.data, initialDeskIds]
  );

  const [fieldForm, setFieldForm] = useState<{ fieldId: number | null } | null>(null);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ENTITIES_QUERY_KEY });

  const save = useMutation({
    mutationFn: async () => {
      if (group) {
        const body: { name: string; active: boolean; deskIds?: number[] } = {
          name: name.trim(),
          active,
        };
        if (appliedIn === 'ticket') body.deskIds = deskIds;
        const res = await api<unknown>(`/entities/${group.id}`, { method: 'PUT', body });
        if (res.error) throw res.error;
        return null;
      }
      const body: { name: string; appliedIn: EntityAppliedIn; deskIds?: number[] } = {
        name: name.trim(),
        appliedIn,
      };
      if (appliedIn === 'ticket' && deskIds.length > 0) body.deskIds = deskIds;
      const res = await api<{ id: number }>('/entities', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (created) => {
      invalidate();
      if (created) {
        onCreated(created.id); // segue para a etapa de campos
      } else {
        onClose();
      }
    },
  });

  const removeGroup = useMutation({
    mutationFn: async () => {
      const res = await api<void>(`/entities/${group?.id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const removeField = useMutation({
    mutationFn: async (fieldId: number) => {
      const res = await api<void>(`/entities/fields/${fieldId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  const toggleDesk = (id: number) => {
    setDeskIds((prev) => (prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]));
  };

  const fields = [...(group?.fields ?? [])].sort((a, b) => a.position - b.position);

  return (
    <div className="space-y-4">
      {/* ── Dados do grupo ──────────────────────────────────────────── */}
      <div className="space-y-1.5">
        <Label htmlFor="entity-group-name">
          Nome <span className="text-destructive">*</span>
        </Label>
        <Input
          id="entity-group-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus={!group}
        />
      </div>

      {appliedIn === 'ticket' ? (
        <div className="space-y-1.5">
          <Label>Mesas</Label>
          {desks.isPending ? (
            <Skeleton className="h-20 w-full" />
          ) : desks.isError ? (
            <p className="text-sm text-destructive">
              Erro ao carregar mesas: {errorMessage(desks.error)}
            </p>
          ) : deskOptions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma mesa ativa cadastrada.</p>
          ) : (
            <div className="max-h-40 overflow-y-auto rounded-md border border-divider p-1.5">
              {deskOptions.map((d) => (
                <label
                  key={d.id}
                  className="flex cursor-pointer items-center gap-2 rounded-sm px-1.5 py-1 text-sm transition-colors hover:bg-accent"
                >
                  <Checkbox
                    checked={deskIds.includes(d.id)}
                    onCheckedChange={() => toggleDesk(d.id)}
                  />
                  <span className={cn('truncate', !d.active && 'text-muted-foreground')}>
                    {d.displayName}
                  </span>
                </label>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Sem mesa selecionada, o grupo vale para todas as mesas.
          </p>
        </div>
      ) : null}

      {group ? (
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
          <Switch checked={active} onCheckedChange={setActive} />
          Grupo ativo
        </label>
      ) : null}

      {/* ── Campos do grupo (após criar) ────────────────────────────── */}
      {group ? (
        <div className="space-y-2 rounded-md border border-divider p-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Campos</h3>
            <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-medium text-muted-foreground tabular-nums">
              {fields.length}
            </span>
            <div className="ml-auto">
              <Button variant="outline" size="sm" onClick={() => setFieldForm({ fieldId: null })}>
                <Plus className="size-3.5" />
                Campo
              </Button>
            </div>
          </div>
          {fields.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">
              Nenhum campo neste grupo. Adicione o primeiro campo.
            </p>
          ) : (
            <ul className="divide-y divide-divider">
              {fields.map((f) => (
                <li key={f.id} className="flex items-center gap-2 py-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                      <span className="truncate font-medium">{f.name}</span>
                      <Badge>{FIELD_TYPE_LABELS[f.fieldType]}</Badge>
                      {f.required ? <Badge variant="warning">Obrigatório</Badge> : null}
                    </div>
                    {f.fieldType === 'single_select' && f.options.length > 0 ? (
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">
                        {f.options.map((o) => o.value).join(', ')}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-0.5">
                    <DeskRowAction
                      label="Editar campo"
                      onClick={() => setFieldForm({ fieldId: f.id })}
                    >
                      <Pencil className="size-3.5" />
                    </DeskRowAction>
                    <DeskRowAction
                      label="Excluir campo"
                      destructive
                      disabled={removeField.isPending}
                      onClick={() => {
                        if (window.confirm(`Excluir o campo "${f.name}" e seus valores?`)) {
                          removeField.mutate(f.id);
                        }
                      }}
                    >
                      <Trash2 className="size-3.5" />
                    </DeskRowAction>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {removeField.error ? (
            <p className="text-sm text-destructive">{errorMessage(removeField.error)}</p>
          ) : null}
          {fieldForm ? (
            <FieldForm
              groupId={group.id}
              field={
                fieldForm.fieldId != null
                  ? (fields.find((f) => f.id === fieldForm.fieldId) ?? null)
                  : null
              }
              onClose={() => setFieldForm(null)}
            />
          ) : null}
        </div>
      ) : null}

      {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
      {removeGroup.error ? (
        <p className="text-sm text-destructive">{errorMessage(removeGroup.error)}</p>
      ) : null}

      <div className="flex items-center gap-2 pt-1">
        {group ? (
          <Button
            variant="outline"
            size="sm"
            disabled={removeGroup.isPending}
            onClick={() => {
              if (
                window.confirm(`Excluir o grupo "${group.name}" e todos os seus campos e valores?`)
              ) {
                removeGroup.mutate();
              }
            }}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="size-3.5" />
            Excluir grupo
          </Button>
        ) : null}
        <div className="ml-auto flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {group ? 'Fechar' : 'Cancelar'}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={save.isPending || !name.trim()}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Salvando…' : group ? 'Salvar' : 'Criar grupo'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Formulário de campo (criar/editar) ──────────────────────────────────────

function FieldForm({
  groupId,
  field,
  onClose,
}: {
  groupId: number;
  field: EntityField | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(field?.name ?? '');
  const [fieldType, setFieldType] = useState<EntityFieldType>(field?.fieldType ?? 'text');
  const [required, setRequired] = useState(field?.required ?? false);
  const [newOptions, setNewOptions] = useState<string[]>([]); // só na criação
  const [optionInput, setOptionInput] = useState('');

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ENTITIES_QUERY_KEY });
  const isSelect = fieldType === 'single_select';

  const save = useMutation({
    mutationFn: async () => {
      if (field) {
        const res = await api<unknown>(`/entities/fields/${field.id}`, {
          method: 'PUT',
          body: { name: name.trim(), required },
        });
        if (res.error) throw res.error;
      } else {
        const res = await api<unknown>(`/entities/${groupId}/fields`, {
          method: 'POST',
          body: {
            name: name.trim(),
            fieldType,
            required,
            ...(isSelect ? { options: newOptions } : {}),
          },
        });
        if (res.error) throw res.error;
      }
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const addOption = useMutation({
    mutationFn: async (value: string) => {
      const res = await api<unknown>(`/entities/fields/${field?.id}/options`, {
        method: 'POST',
        body: { value },
      });
      if (res.error) throw res.error;
    },
    onSuccess: () => {
      invalidate();
      setOptionInput('');
    },
  });

  const removeOption = useMutation({
    mutationFn: async (optionId: number) => {
      const res = await api<void>(`/entities/fields/${field?.id}/options/${optionId}`, {
        method: 'DELETE',
      });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  const addLocalOption = () => {
    const value = optionInput.trim();
    if (!value || newOptions.includes(value)) return;
    setNewOptions([...newOptions, value]);
    setOptionInput('');
  };

  const onOptionKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (field) {
      if (optionInput.trim()) addOption.mutate(optionInput.trim());
    } else {
      addLocalOption();
    }
  };

  // criação de lista de seleção exige ao menos uma opção
  const valid = name.trim() !== '' && (field != null || !isSelect || newOptions.length > 0);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    save.mutate();
  };

  const mutationError = save.error ?? addOption.error ?? removeOption.error;

  return (
    <form onSubmit={submit} className="space-y-3 rounded-md border border-divider bg-muted/40 p-3">
      <p className="text-sm font-semibold">{field ? `Editar campo` : 'Novo campo'}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="entity-field-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="entity-field-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="entity-field-type">Tipo</Label>
          {field ? (
            <p className="flex h-9 items-center text-sm text-muted-foreground">
              {FIELD_TYPE_LABELS[field.fieldType]} (não pode ser alterado)
            </p>
          ) : (
            <Select
              id="entity-field-type"
              value={fieldType}
              onChange={(e) => setFieldType(e.target.value as EntityFieldType)}
            >
              {FIELD_TYPES.map((t) => (
                <option key={t} value={t}>
                  {FIELD_TYPE_LABELS[t]}
                </option>
              ))}
            </Select>
          )}
        </div>
      </div>

      {isSelect ? (
        <div className="space-y-1.5">
          <Label htmlFor="entity-field-option">
            Opções {field ? null : <span className="text-destructive">*</span>}
          </Label>
          <div className="flex flex-wrap gap-1.5">
            {field
              ? field.options.map((o) => (
                  <OptionChip
                    key={o.id}
                    value={o.value}
                    disabled={removeOption.isPending}
                    onRemove={() => removeOption.mutate(o.id)}
                  />
                ))
              : newOptions.map((value) => (
                  <OptionChip
                    key={value}
                    value={value}
                    onRemove={() => setNewOptions(newOptions.filter((v) => v !== value))}
                  />
                ))}
          </div>
          <div className="flex items-center gap-1.5">
            <Input
              id="entity-field-option"
              value={optionInput}
              onChange={(e) => setOptionInput(e.target.value)}
              onKeyDown={onOptionKeyDown}
              placeholder="Nova opção"
              className="h-8"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!optionInput.trim() || addOption.isPending}
              onClick={() => (field ? addOption.mutate(optionInput.trim()) : addLocalOption())}
            >
              <Plus className="size-3.5" />
              Adicionar
            </Button>
          </div>
        </div>
      ) : null}

      <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
        <Switch checked={required} onCheckedChange={setRequired} />
        Preenchimento obrigatório
      </label>

      {mutationError ? (
        <p className="text-sm text-destructive">{errorMessage(mutationError)}</p>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" size="sm" disabled={save.isPending || !valid}>
          {save.isPending ? 'Salvando…' : field ? 'Salvar campo' : 'Adicionar campo'}
        </Button>
      </div>
    </form>
  );
}

function OptionChip({
  value,
  disabled,
  onRemove,
}: {
  value: string;
  disabled?: boolean;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-sm border border-border bg-card px-1.5 py-0.5 text-xs font-medium">
      {value}
      <button
        type="button"
        aria-label={`Remover opção ${value}`}
        disabled={disabled}
        onClick={onRemove}
        className="text-muted-foreground transition-colors hover:text-destructive disabled:pointer-events-none disabled:opacity-40"
      >
        <X className="size-3" />
      </button>
    </span>
  );
}
